// src/marketing/content/ContentDetailPage.tsx
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type {
  Attachment, ContentFormat, ContentKind, ContentPlatform, ContentStatus, TemplateLanguage,
} from '../../lib/types'
import { useAppStore } from '../../shell/appStore'
import { renderMarkdown } from '../docs/markdown'
import { uploadAttachment, useAttachments } from '../../crm/hooks/useAttachments'
import { useCreators } from '../../crm/api'
import { useCampaigns } from '../../crm/hooks/useCampaigns'
import {
  useContent, useCreateContent, useUpdateContent, useDeleteContent,
  type CreateContentInput,
} from './useContents'
import {
  CONTENT_STATUSES, CONTENT_STATUS_LABELS, KIND_LABELS, FORMAT_LABELS, PLATFORM_LABELS, liveLabel,
} from './statuses'
import { GenerationPanel } from './GenerationPanel'

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--elevated)',
  border: '1px solid var(--line-strong)',
  borderRadius: '7px',
  padding: '0.5rem 0.75rem',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  outline: 'none',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--muted)',
  marginBottom: '0.375rem',
}

const sectionStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: '10px',
  padding: '1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
}

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--dim)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const row2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }
const row3: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }

type FormState = {
  title: string
  kind: ContentKind
  format: ContentFormat
  platform: ContentPlatform
  language: TemplateLanguage | ''
  status: ContentStatus
  hook: string
  bodyMd: string
  url: string
  plannedDate: string
  liveDate: string
  budgetAllocated: string
  campaignId: string
  creatorId: string
  views: string
  notes: string
}

const DEFAULT_FORM: FormState = {
  title: '', kind: 'organic', format: 'video', platform: 'tiktok', language: '',
  status: 'idea', hook: '', bodyMd: '', url: '', plannedDate: '', liveDate: '',
  budgetAllocated: '', campaignId: '', creatorId: '', views: '', notes: '',
}

export function ContentDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(id)
  const appId = useAppStore((s) => s.activeAppId)

  const { data: existing, isLoading } = useContent(id ?? null)
  const createContent = useCreateContent()
  const updateContent = useUpdateContent(id ?? '')
  const deleteContent = useDeleteContent()
  const { data: creators = [] } = useCreators(undefined, { type: 'ugc_creator' })
  const { data: campaigns = [] } = useCampaigns(appId)
  const { data: attachments = [] } = useAttachments('content', id ?? null)

  const [form, setForm] = useState<FormState>(() => ({
    ...DEFAULT_FORM,
    plannedDate: searchParams.get('date') ?? '',
  }))
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadedNow, setUploadedNow] = useState<Attachment[]>([])

  useEffect(() => {
    if (existing) {
      setForm({
        title: existing.title,
        kind: existing.kind,
        format: existing.format,
        platform: existing.platform,
        language: existing.language ?? '',
        status: existing.status,
        hook: existing.hook ?? '',
        bodyMd: existing.bodyMd,
        url: existing.url ?? '',
        plannedDate: existing.plannedDate ?? '',
        liveDate: existing.liveDate ?? '',
        budgetAllocated: existing.budgetAllocated != null ? String(existing.budgetAllocated) : '',
        campaignId: existing.campaignId ?? '',
        creatorId: existing.creatorId ?? '',
        views: existing.views != null ? String(existing.views) : '',
        notes: existing.notes ?? '',
      })
    }
  }, [existing])

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!appId || !form.title.trim()) {
      setError('Le titre est requis.')
      return
    }
    const payload: CreateContentInput = {
      appId,
      title: form.title.trim(),
      kind: form.kind,
      format: form.format,
      platform: form.platform,
      language: form.language || null,
      status: form.status,
      hook: form.hook.trim() || null,
      bodyMd: form.bodyMd,
      url: form.url.trim() || null,
      plannedDate: form.plannedDate || null,
      liveDate: form.liveDate || null,
      budgetAllocated: form.budgetAllocated ? Number(form.budgetAllocated) : null,
      campaignId: form.campaignId || null,
      creatorId: form.creatorId || null,
      views: form.views ? Number(form.views) : null,
      notes: form.notes.trim() || null,
    }
    try {
      if (isEdit) {
        const { appId: _appId, ...updateBody } = payload
        await updateContent.mutateAsync(updateBody)
      } else {
        await createContent.mutateAsync(payload)
      }
      navigate('/marketing/content')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue.')
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !id) return
    setUploading(true)
    setError(null)
    try {
      // `kind: 'content'` = nature du fichier (colonne attachments.kind) ;
      // `linkedType: 'content'` = lien vers la table contents. Homonymie assumée.
      const att = await uploadAttachment({ file, kind: 'content', linkedType: 'content', linkedId: id })
      setUploadedNow((prev) => [...prev, att])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur upload')
    } finally {
      setUploading(false)
    }
  }

  function handleDelete() {
    if (!id) return
    if (!confirm(`Supprimer le contenu "${form.title}" ? Cette action est irréversible.`)) return
    deleteContent.mutate(id, { onSuccess: () => navigate('/marketing/content') })
  }

  const allAttachments = [...attachments, ...uploadedNow.filter((u) => !attachments.some((a) => a.id === u.id))]
  const isPending = createContent.isPending || updateContent.isPending

  if (isEdit && isLoading) {
    return <div style={{ padding: '2rem 2.5rem' }}><p style={{ color: 'var(--muted)' }}>Chargement…</p></div>
  }

  return (
    <div style={{ padding: '2rem 2.5rem', maxWidth: '980px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.75rem' }}>
        <button type="button" onClick={() => navigate('/marketing/content')} style={{ background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '7px', padding: '0.375rem 0.625rem', color: 'var(--muted)', fontSize: '0.8125rem', fontFamily: 'inherit', cursor: 'pointer' }}>
          ← Retour
        </button>
        <h1 style={{ fontSize: '1.375rem', fontWeight: 600, color: 'var(--fg)', margin: 0, letterSpacing: '-0.025em' }}>
          {isEdit ? 'Modifier le contenu' : 'Nouveau contenu'}
        </h1>
        {isEdit && (
          <button type="button" onClick={handleDelete} style={{ marginLeft: 'auto', background: 'rgba(217,83,30,0.12)', border: '1px solid rgba(217,83,30,0.25)', borderRadius: '7px', padding: '0.375rem 0.75rem', color: 'var(--arancio)', fontSize: '0.8125rem', fontFamily: 'inherit', cursor: 'pointer' }}>
            Supprimer
          </button>
        )}
      </div>

      <form onSubmit={(e) => { void handleSubmit(e) }} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <section style={sectionStyle}>
          <div>
            <label style={labelStyle} htmlFor="cnt-title">Titre *</label>
            <input id="cnt-title" type="text" required value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Reel détection de plateau" style={inputStyle} />
          </div>
          <div style={row2}>
            <div>
              <label style={labelStyle} htmlFor="cnt-kind">Type</label>
              <select id="cnt-kind" value={form.kind} onChange={(e) => set('kind', e.target.value as ContentKind)} style={inputStyle}>
                {Object.entries(KIND_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="cnt-status">Statut</label>
              <select id="cnt-status" value={form.status} onChange={(e) => set('status', e.target.value as ContentStatus)} style={inputStyle}>
                {CONTENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s === 'live' ? liveLabel(form.kind) : CONTENT_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={row2}>
            <div>
              <label style={labelStyle} htmlFor="cnt-platform">Plateforme</label>
              <select id="cnt-platform" value={form.platform} onChange={(e) => set('platform', e.target.value as ContentPlatform)} style={inputStyle}>
                {Object.entries(PLATFORM_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="cnt-format">Format</label>
              <select id="cnt-format" value={form.format} onChange={(e) => set('format', e.target.value as ContentFormat)} style={inputStyle}>
                {Object.entries(FORMAT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={row2}>
            <div>
              <label style={labelStyle} htmlFor="cnt-lang">Langue</label>
              <select id="cnt-lang" value={form.language} onChange={(e) => set('language', e.target.value as TemplateLanguage | '')} style={inputStyle}>
                <option value="">—</option>
                <option value="fr">FR</option>
                <option value="en">EN</option>
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="cnt-hook">Accroche</label>
              <input id="cnt-hook" type="text" value={form.hook} onChange={(e) => set('hook', e.target.value)} placeholder="Vous êtes en plateau depuis 3 semaines…" style={inputStyle} />
            </div>
          </div>
        </section>

        <section style={sectionStyle}>
          <p style={sectionTitleStyle}>Script / corps</p>
          <div style={row2}>
            <textarea
              aria-label="Script"
              rows={12}
              value={form.bodyMd}
              onChange={(e) => set('bodyMd', e.target.value)}
              placeholder={'## Hook\n\nTexte du script en Markdown…'}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
            />
            <div>
              <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0 0 0.25rem' }}>Aperçu</p>
              <div
                data-testid="content-preview"
                style={{ border: '1px dashed var(--line-strong)', borderRadius: '7px', padding: '0.75rem', fontSize: '0.8125rem', color: 'var(--fg)', minHeight: '200px' }}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(form.bodyMd) }}
              />
            </div>
          </div>
        </section>

        <section style={sectionStyle}>
          <p style={sectionTitleStyle}>Planification & diffusion</p>
          <div style={row2}>
            <div>
              <label style={labelStyle} htmlFor="cnt-planned">Date prévue</label>
              <input id="cnt-planned" type="date" value={form.plannedDate} onChange={(e) => set('plannedDate', e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="cnt-live">Date effective</label>
              <input id="cnt-live" type="date" value={form.liveDate} onChange={(e) => set('liveDate', e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={row3}>
            {form.kind === 'ad' && (
              <div>
                <label style={labelStyle} htmlFor="cnt-budget">Budget alloué (€)</label>
                <input id="cnt-budget" type="number" min={0} step={0.01} value={form.budgetAllocated} onChange={(e) => set('budgetAllocated', e.target.value)} placeholder="150" style={inputStyle} />
              </div>
            )}
            <div>
              <label style={labelStyle} htmlFor="cnt-url">URL publiée</label>
              <input id="cnt-url" type="url" value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://…" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="cnt-views">Vues (relevé manuel)</label>
              <input id="cnt-views" type="number" min={0} value={form.views} onChange={(e) => set('views', e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={row2}>
            <div>
              <label style={labelStyle} htmlFor="cnt-campaign">Campagne</label>
              <select id="cnt-campaign" value={form.campaignId} onChange={(e) => set('campaignId', e.target.value)} style={inputStyle}>
                <option value="">—</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="cnt-creator">Créateur lié (UGC)</label>
              <select id="cnt-creator" value={form.creatorId} onChange={(e) => set('creatorId', e.target.value)} style={inputStyle}>
                <option value="">—</option>
                {creators.map((c) => (
                  <option key={c.id} value={c.id}>{c.displayName}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle} htmlFor="cnt-notes">Notes</label>
            <input id="cnt-notes" type="text" value={form.notes} onChange={(e) => set('notes', e.target.value)} style={inputStyle} />
          </div>
        </section>

        {isEdit && (
          <section style={sectionStyle}>
            <p style={sectionTitleStyle}>Fichiers</p>
            <label htmlFor="cnt-file" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.75rem', background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '6px', fontSize: '0.8125rem', color: 'var(--muted)', cursor: 'pointer', width: 'fit-content' }}>
              {uploading ? 'Chargement…' : 'Ajouter un fichier'}
            </label>
            <input id="cnt-file" type="file" style={{ display: 'none' }} onChange={(e) => { void handleFileChange(e) }} />
            {allAttachments.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                {allAttachments.map((a) => (
                  <a key={a.id} href={`/api/attachments/${a.id}`} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--arancio)', background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '4px', padding: '0.125rem 0.5rem', textDecoration: 'none' }}>
                    {a.filename}
                  </a>
                ))}
              </div>
            )}
          </section>
        )}

        {isEdit && id && (
          <GenerationPanel key={id} contentId={id} defaultPrompt={existing?.hook ?? ''} />
        )}

        {error && (
          <p style={{ background: 'rgba(217,83,30,0.1)', border: '1px solid rgba(217,83,30,0.25)', borderRadius: '7px', padding: '0.625rem 0.875rem', color: 'var(--arancio)', fontSize: '0.875rem', margin: 0 }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => navigate('/marketing/content')} style={{ background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '7px', padding: '0.5rem 1rem', color: 'var(--muted)', fontSize: '0.875rem', fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}>
            Annuler
          </button>
          <button type="submit" disabled={isPending} style={{ background: isPending ? 'var(--dim)' : 'var(--arancio)', border: 'none', borderRadius: '7px', padding: '0.5rem 1.25rem', color: '#fff', fontSize: '0.875rem', fontWeight: 600, fontFamily: 'inherit', cursor: isPending ? 'not-allowed' : 'pointer' }}>
            {isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  )
}
